import { useMutation } from '@apollo/client'
import { datadogLogs } from '@datadog/browser-logs'
import { useState } from 'react'
import { useAddFdChatSpecification } from '../../containers/Page/FdChat/FdChat.hooks'
import { defaultFdChatSpecification } from '../../containers/Page/FdChat/FdChat.model'
import { ProjectType } from '../../data/gql-gen/dashboard/graphql'
import { LoggerErrorType } from '../../data/logger'
import { CREATE_PROJECT } from '../../modules/Project/Project.mutations'
import { SEARCH_PROJECTS_QUERY_NAME } from '../../modules/Project/Project.query'
import { captureApolloError } from '../../utils/HelperFunctions'
import UserSession from '../../utils/UserSession'
import { composeMarketName, countryLabel } from '../../utils/marketFlags'
import useGetUserAndAccount from '../useGetUserAndAccount'
import { useCreateInitialQuestionnaire } from './useCreateInitialQuestionnaire'
import { useNavigateToInitialProjectPage } from './useNavigateToInitialProjectPage'

export const useCreateProject = () => {
  const [loadingCreateProject, setLoadingCreateProject] = useState(false)
  const [createProject] = useMutation(CREATE_PROJECT, {
    context: { clientName: 'dashboard' },
    refetchQueries: [SEARCH_PROJECTS_QUERY_NAME],
    onCompleted: (data) => {
      datadogLogs.logger.info('Project created', {
        data
      })
    },
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloMutation, 'createProject', error)
    }
  })
  const {
    userName,
    loading: loadingGetUserAndAccount,
    markup
  } = useGetUserAndAccount()
  const { createInitialQuestionnaire } = useCreateInitialQuestionnaire()

  const { addFdChatSpecification } = useAddFdChatSpecification()
  const navigateToInitialProjectPage = useNavigateToInitialProjectPage()

  const handleCreateProject = async ({
    title,
    projectType,
    countryCode,
    countryLanguage,
    samplesNeeded
  }: {
    title?: string
    projectType: ProjectType
    countryCode?: string
    countryLanguage?: string
    samplesNeeded?: number
  }) => {
    setLoadingCreateProject(true)

    const marketCountryCode = (countryCode || 'GB').toUpperCase()
    // A multi-market project's first survey is fielded alongside the markets
    // added later (the country picker is hidden for it, so it is always GB), so
    // it is named as a market rather than after the project — that is what
    // gives it an editable audience label. The prefix comes from countryLabel,
    // the same source isCountryCodedMarketName checks it against.
    const surveyName =
      projectType === ProjectType.MultiMarketV2
        ? composeMarketName({
            countryPrefix: countryLabel(marketCountryCode),
            audienceLabel: '',
            countryCode: marketCountryCode
          })
        : undefined

    const createProjectResult = await createProject({
      variables: {
        name: title || 'My Project',
        description: '',
        projectType: projectType,
        accountId: UserSession.getAccountId(),
        solutionId: UserSession.getSolutionId(),
        creator: userName || 'N/A',
        surveyName
      }
    })

    const createdProject = createProjectResult.data?.createProject

    if (!createdProject) {
      throw new Error('No project created')
    }

    const projectId = createdProject.projectId
    const surveyId = createdProject.surveys[0].surveyId || 'N/A'

    const isFdChat = projectType === ProjectType.FdChat

    // A tracker's questionnaire belongs to a wave, so it is created with wave 1.
    if (projectType !== ProjectType.Tracker) {
      await createInitialQuestionnaire({
        projectId,
        surveyId,
        projectType,
        accountMarkup: markup,
        countryCode,
        countryLanguage,
        samplesNeeded
      })
    }

    // if the project is an FD Chat project, add the default FD Chat specification and set the preset audience to Custom
    if (isFdChat) {
      await addFdChatSpecification({
        variables: {
          input: {
            projectId,
            surveyId,
            fdChatSpecification: { value: { ...defaultFdChatSpecification } }
          }
        }
      })
    }

    navigateToInitialProjectPage(createdProject)

    setLoadingCreateProject(false)
  }

  return {
    loadingGetUserAndAccount,
    loadingCreateProject,
    handleCreateProject
  }
}

export default useCreateProject
